import React, { useEffect, useRef, useState } from 'react';
import HorizontalNavProps, { NavLink } from "./props";
import style from "./style.module.scss";
import classNames from '../../../utils/classNames';
import { ArrowsHandler } from './components/ArrowsHandler';
import Link from '../../private/Link';
import LinkProps from '../../private/Link/props';
import config from './config.json';

function HorizontalNav(props: HorizontalNavProps) {
  const [hideLeft, setHideLeft] = useState(true);
  const [hideRight, setHideRight] = useState(false);
  const [showArrowButtons, setShowArrowButtons] = useState(false);
  const scrollableRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const scrollableEl = scrollableRef.current;
    if (!scrollableEl || props.hideArrowButtons) {
      setShowArrowButtons(false);
      return;
    }

    const checkArrows = () => {
      const hasOverflow = scrollableEl.scrollWidth > scrollableEl.clientWidth;
      setShowArrowButtons(hasOverflow);

      if (hasOverflow) {
        setHideLeft(scrollableEl.scrollLeft <= 0);
        const scrollEnd = scrollableEl.scrollWidth - scrollableEl.clientWidth;
        setHideRight(scrollableEl.scrollLeft >= scrollEnd - 1);
      } else {
        setHideLeft(true);
        setHideRight(true);
      }
    };

    checkArrows();

    scrollableEl.addEventListener('scroll', checkArrows, { passive: true });

    const resizeObserver = new ResizeObserver(checkArrows);
    resizeObserver.observe(scrollableEl);

    return () => {
      scrollableEl.removeEventListener('scroll', checkArrows);
      resizeObserver.disconnect();
    };
  }, [props.links, props.hideArrowButtons]);

  return (
    <div className={classNames('horizontalNav', style)} data-e2e='horizontal-navigation-component'>
      <div className={classNames('scrollNav', style )} ref={scrollableRef}>
        {props.links && props.links.map((link, index) => (
          <div className={classNames('navigation-item', style)} key={index}>
            <Link {...getLinkProps(link)} />
          </div>
        ))}
      </div>
      {showArrowButtons && (
        <div className={classNames('arrows-handler', style)}>
          <ArrowsHandler
            hideLeft={hideLeft}
            hideRight={hideRight}
            leftButtonAltText={props.leftButtonAltText}
            rightButtonAltText={props.rightButtonAltText}
            scrollableRefCurrent={scrollableRef.current}
            leftButtonAttributes={props.leftButtonAttributes}
            rightButtonAttributes={props.rightButtonAttributes}
          />
        </div>
      )}
    </div>
  );
}

const getLinkProps = (props: NavLink): LinkProps => ({
  ...props,
  classNames: config.Link.classNames,
  hyperlinkAttributes: {
    ...props.hyperlinkAttributes,
    'data-active': props.active
  }
});

export default React.memo(HorizontalNav);
